<template>
  <div class="flex items-center space-x-3 p-3 hover:bg-gray-50 rounded-lg group" :data-item-id="item.id">
    <!-- Drag Handle -->
    <div class="drag-handle cursor-move text-gray-400 hover:text-gray-600 opacity-0 group-hover:opacity-100 transition-opacity">
      <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
        <path d="M11 18c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2zm-2-8c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0-6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm6 4c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
      </svg>
    </div>
    
    <!-- Checkbox -->
    <input
      :checked="item.completed"
      @change="toggleCompleted"
      type="checkbox"
      class="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
    >
    
    <!-- Item Content -->
    <div class="flex-1 min-w-0">
      <div class="flex items-center space-x-2">
        <span 
          :class="[
            'font-medium cursor-pointer',
            item.completed 
              ? 'text-gray-500 line-through' 
              : 'text-gray-900'
          ]"
          @click="editItemName"
        >
          {{ item.name }}
        </span>
        <span 
          v-if="item.quantity && item.quantity > 1"
          :class="[
            'text-sm px-2 py-1 bg-gray-100 rounded-full cursor-pointer',
            item.completed ? 'text-gray-400' : 'text-gray-600'
          ]"
          @click="editItemQuantity"
        >
          {{ item.quantity }}x
        </span>
      </div>
      <p 
        v-if="item.notes"
        :class="[
          'text-sm mt-1 cursor-pointer',
          item.completed ? 'text-gray-400' : 'text-gray-600'
        ]"
        @click="editItemNotes"
      >
        {{ item.notes }}
      </p>
    </div>

    <!-- Actions -->
    <div class="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
      <button 
        @click="editItem"
        class="text-gray-400 hover:text-gray-600 transition-colors"
        title="Edit item"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      </button>
      <button 
        @click="confirmDelete"
        class="text-gray-400 hover:text-red-600 transition-colors"
        title="Delete item"
      >
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      </button>
    </div>

    <!-- Edit Item Dialog -->
    <dialog 
      ref="editItemDialog" 
      class="backdrop:bg-black backdrop:bg-opacity-50 p-0 rounded-lg shadow-xl max-w-md w-full border-0"
      @click="handleDialogBackdropClick"
    >
      <div class="p-6 bg-white rounded-lg">
        <h3 class="text-lg font-semibold mb-4">Edit Item</h3>
        <form @submit.prevent="handleUpdateItem">
          <input
            ref="itemNameInput"
            v-model="editItemNameValue"
            type="text"
            placeholder="Item name"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4"
            required
            @keydown.escape="closeEditItemDialog"
          >
          <input
            v-model="editItemQuantityValue"
            type="number"
            placeholder="Quantity (optional)"
            min="1"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4"
            @keydown.escape="closeEditItemDialog"
          >
          <textarea
            v-model="editItemNotesValue"
            placeholder="Notes (optional)"
            rows="3"
            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4 resize-none"
            @keydown.escape="closeEditItemDialog"
          ></textarea>
          <div class="flex justify-end space-x-2">
            <button 
              type="button"
              @click="closeEditItemDialog"
              class="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button 
              type="submit"
              class="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </dialog>

    <!-- Confirm Delete Dialog -->
    <dialog 
      ref="confirmDeleteDialog" 
      class="backdrop:bg-black backdrop:bg-opacity-50 p-0 rounded-lg shadow-xl max-w-md w-full border-0"
      @click="handleDialogBackdropClick"
    >
      <div class="p-6 bg-white rounded-lg">
        <h3 class="text-lg font-semibold mb-4 text-red-600">Delete Item</h3>
        <p class="text-gray-600 mb-6">
          Are you sure you want to delete "{{ item.name }}"? This action cannot be undone.
        </p>
        <div class="flex justify-end space-x-2">
          <button 
            @click="closeConfirmDeleteDialog"
            class="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            Cancel
          </button>
          <button 
            @click="handleDeleteItem"
            class="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
          >
            Delete
          </button>
        </div>
      </div>
    </dialog>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import type { Item } from '@/types'

const props = defineProps<{
  item: Item
}>()

const emit = defineEmits<{
  'update-item': [payload: { id: string, data: Partial<Item> }]
  'delete-item': [id: string]
}>()

// Dialog refs
const editItemDialog = ref<HTMLDialogElement>()
const confirmDeleteDialog = ref<HTMLDialogElement>()
const itemNameInput = ref<HTMLInputElement>()

// Form data
const editItemNameValue = ref('')
const editItemQuantityValue = ref<number | undefined>()
const editItemNotesValue = ref('')

// Methods
const toggleCompleted = () => {
  emit('update-item', {
    id: props.item.id,
    data: { completed: !props.item.completed }
  })
}

const editItem = () => {
  editItemNameValue.value = props.item.name
  editItemQuantityValue.value = props.item.quantity && props.item.quantity > 1 ? props.item.quantity : undefined
  editItemNotesValue.value = props.item.notes || ''
  editItemDialog.value?.showModal()
  setTimeout(() => {
    itemNameInput.value?.focus()
    itemNameInput.value?.select()
  }, 100)
}

const editItemName = () => {
  editItem()
}

const editItemQuantity = () => {
  editItem()
}

const editItemNotes = () => {
  editItem()
}

const confirmDelete = () => {
  confirmDeleteDialog.value?.showModal()
}

const handleUpdateItem = () => {
  if (editItemNameValue.value.trim()) {
    emit('update-item', {
      id: props.item.id,
      data: {
        name: editItemNameValue.value.trim(),
        quantity: editItemQuantityValue.value,
        notes: editItemNotesValue.value.trim() || undefined
      }
    })
    closeEditItemDialog()
  }
}

const handleDeleteItem = () => {
  emit('delete-item', props.item.id)
  closeConfirmDeleteDialog()
}

const closeEditItemDialog = () => {
  editItemDialog.value?.close()
  editItemNameValue.value = ''
  editItemQuantityValue.value = undefined
  editItemNotesValue.value = ''
}

const closeConfirmDeleteDialog = () => {
  confirmDeleteDialog.value?.close()
}

// Handle dialog backdrop clicks (close on click outside)
const handleDialogBackdropClick = (event: MouseEvent) => {
  const dialog = event.target as HTMLDialogElement
  if (event.target === dialog) {
    dialog.close()
    // Clean up form data
    if (dialog === editItemDialog.value) {
      editItemNameValue.value = ''
      editItemQuantityValue.value = undefined
      editItemNotesValue.value = ''
    }
  }
}
</script>